import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import { tns } from 'tiny-slider/src/tiny-slider';
import * as geoJsonBoundsMod from 'geojson-bounds';
const geoJsonBounds = geoJsonBoundsMod.default || geoJsonBoundsMod;

import {
    map,
    bounds,
    clusters,
    $dataPanelTns,
    noResultsText,
    noResultsTextMessage,
    mapElement,
    searchInput,
    categSelects,
    mapVenueCards,
    sidePanelOfCategs,
    markerMode,
    infowindow,
    infowindowTimeout,
    highlightZone,
    $side_panel,
    setBounds,
    setClusters,
    setDataPanelTns,
    setHighlightZone,
    setInfowindow,
    setInfowindowTimeout,
} from './_context.js';
import { applyMapPadding, fitBoundsOrCenterSingle, getMapPadding } from './_map-view.js';
import { checkWithinBounds, getGuaranteedInsidePoint } from './_geo-utils.js';
import { createClusterRenderer } from './_cluster-renderer.js';
import { HtmlMarker, adjustMarkerVisibility, setIconInHtmlMarker } from './_html-marker.js';
import { closeInfoWindow, generateInfoWindowContent } from './_infowindow.js';
import { resolveDetailUrl } from './_permalink.js';
import {
    mergeCategoryFilterSearchParamsIntoUrl,
    stripCategoryParamsFromSearchParams,
    stripLieuQueryParams,
    syncCategoryHistoryReplace,
    urlHasLieuQueryParam,
} from './_category-history.js';

/** True while completing closePin after a programmatic history.back(). */
let ignoreNextPopstate = false;

/**
 * Update history to `lieu=` plus active category params (`categorie`, etc.).
 */
function syncHistoryOnOpenPin(id, elementToOpen) {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        const u = new URL(window.location.href);
        stripLieuQueryParams(u.searchParams);
        stripCategoryParamsFromSearchParams(u.searchParams);
        const slug =
            elementToOpen &&
            elementToOpen.slug &&
            String(elementToOpen.slug).trim() !== ''
                ? String(elementToOpen.slug).trim()
                : String(id);
        u.searchParams.set('lieu', slug);
        mergeCategoryFilterSearchParamsIntoUrl(u);
        const path = u.pathname + u.search + u.hash;
        const currentFull =
            window.location.pathname + window.location.search + window.location.hash;
        if (path === currentFull) {
            return;
        }
        const state = { actCartePin: String(id) };
        const prevPin = window.history.state && window.history.state.actCartePin;
        if (prevPin == null) {
            window.history.pushState(state, '', path);
        } else {
            window.history.replaceState(state, '', path);
        }
    } catch (e) {
        /* ignore */
    }
}

/** When closing pin from filter/redraw, drop lieu from the query without history.back(). */
function restoreMapUrlIfPinnedHistory() {
    if (typeof window === 'undefined') {
        return;
    }
    if (!window.history.state || window.history.state.actCartePin == null) {
        return;
    }
    try {
        const u = new URL(window.location.href);
        stripLieuQueryParams(u.searchParams);
        window.history.replaceState(null, '', u.pathname + u.search + u.hash);
    } catch (e) {
        /* ignore */
    }
}

/** Re-use one polyline for enableLines (avoid stacking overlays on redoMarkers). */
let coordsFlightPolyline = null;
/** Re-use geolocation marker (avoid stacking markers on redoMarkers). */
let geolocateUserMarker = null;

function setGeolocateUserMarker(marker) {
    if (geolocateUserMarker) {
        try {
            geolocateUserMarker.setMap(null);
        } catch (err) {}
    }
    geolocateUserMarker = marker;
}

export function redoMarkers(closePins = true) {
    const beforeRedoEvent = new CustomEvent('actMap:beforeRedoMarkers', {
        detail: {
            closePins: closePins,
            mapData: actMapData,
            map: map,
        },
    });
    document.dispatchEvent(beforeRedoEvent);

    if (closePins) {
        closeInfoWindow();
        /** Avoid clearing `data-open-pin` while `?lieu=` is still in the URL (first paint / reload). */
        if (!urlHasLieuQueryParam()) {
            closePin(undefined, { skipRedoMarkers: true, skipHistorySync: true });
        }
    }

    let fitBoundsFlag = false;
    setBounds(new google.maps.LatLngBounds());

    if (!clusters) {
        setClusters(
            new MarkerClusterer({
                map: map,
                renderer: createClusterRenderer(),
                algorithm: new SuperClusterAlgorithm({
                    maxZoom: zoomOnOpen - 1,
                    radius: clusterRadius,
                }),
                onClusterClick(_, cluster, clusterMap) {
                    closePin(false);
                    clusterMap.fitBounds(cluster.bounds, 32);
                },
            }),
        );

        google.maps.event.addListener(clusters, 'clusteringend', adjustMarkerVisibility);
    }

    clusters.clearMarkers(true);

    /** One pass: venue id → elements (avoids N× querySelectorAll per redo). */
    const venueCardsById = new Map();
    document.querySelectorAll('.map-venue-card[data-id]').forEach((node) => {
        const id = node.getAttribute('data-id');
        if (!venueCardsById.has(id)) venueCardsById.set(id, []);
        venueCardsById.get(id).push(node);
    });

    /** Category slug → [data-categ] element cache for levelbased search. */
    const categNodeCache = new Map();
    function getCategNode(categ) {
        if (categNodeCache.has(categ)) return categNodeCache.get(categ);
        const el = document.querySelector('[data-categ="' + categ + '"]');
        categNodeCache.set(categ, el);
        return el;
    }

    const searchRaw = searchInput[0] && searchInput[0].value ? searchInput[0].value : '';
    const searchLc = searchRaw.toLowerCase();
    let searchHighlightRe = null;
    if (searchRaw) {
        try {
            searchHighlightRe = new RegExp(`()(${searchRaw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})()`, 'ig');
        } catch (e) {
            searchHighlightRe = null;
        }
    }

    let currentOpenPin = null;
    if (mapElement[0].getAttribute('data-open-pin')) {
        if (actMapData.length) {
            actMapData.forEach(function (e) {
                if (mapElement[0].getAttribute('data-open-pin') == e.id) {
                    currentOpenPin = e;
                }
            });
        }
    }

    if (actMapData.length) {
        const additionnalCateg = document.getElementsByTagName('body')[0].getAttribute('data-additionnal-categ')
            ? document.getElementsByTagName('body')[0].getAttribute('data-additionnal-categ')
            : '';

        let coordinatesData = [];

        actMapData.forEach(function (e) {
            let addThisElement = true;
            if (categSelects) {
                let isSelectedInFilters = false;
                Array.prototype.forEach.call(categSelects, function (el) {
                    if (el.value.length > 0 && addThisElement) {
                        isSelectedInFilters = true;
                        let needRemoval = true;
                        let hits = 0;
                        let hitlvl1 = 0;
                        let openlvl2 = 0;
                        Array.prototype.forEach.call(el.value, function (categ) {
                            if (categSearchType == 'AND') {
                                if (e.categslugs) {
                                    if (e.categslugs.includes(categ)) {
                                        hits++;
                                    }
                                }
                            }
                            if (categSearchType == 'levelbased') {
                                if (e.categslugs) {
                                    let categEl = getCategNode(categ);
                                    if (categEl) {
                                        if (categEl.parentElement.classList.contains('side-panel__categ_2')) {
                                            openlvl2++;
                                        }
                                    }
                                    if (e.categslugs.includes(categ)) {
                                        if (categEl) {
                                            if (categEl.parentElement.classList.contains('side-panel__categ_2')) {
                                                hits++;
                                            }
                                            if (categEl.parentElement.classList.contains('side-panel__categ_1')) {
                                                hitlvl1++;
                                            }
                                        }
                                    }
                                }
                            } else {
                                if (e.categslugs) {
                                    if (e.categslugs.includes(categ)) {
                                        needRemoval = false;
                                    }
                                }
                            }
                        });

                        if (categSearchType == 'AND') {
                            if (hits != el.value.length) {
                                addThisElement = false;
                            }
                        }
                        if (categSearchType == 'levelbased') {
                            let removal = true;
                            if (openlvl2 == 0 && hitlvl1 != 0) {
                                removal = false;
                            }
                            if (hits != 0) {
                                removal = false;
                            }
                            if (removal) {
                                addThisElement = false;
                            }
                        } else {
                            if (needRemoval) {
                                addThisElement = false;
                            }
                        }
                    }
                });
                if (additionnalCateg) {
                    isSelectedInFilters = true;
                }

                if (e.showonlywhenselected && !isSelectedInFilters) {
                    addThisElement = false;
                }
            }

            if (additionnalCateg && addThisElement) {
                if (!e.categslugs.includes(additionnalCateg)) {
                    addThisElement = false;
                }
            }

            let searchText = '';
            if (searchInput[0]) {
                if (searchLc && addThisElement) {
                    searchText = searchRaw;
                    let rue = e.rue ? e.rue.toLowerCase() : '';
                    let address = e.address ? e.address.toLowerCase() : '';
                    let excerpt = e.excerpt ? e.excerpt.toLowerCase() : '';

                    if (e.categ) {
                        if (
                            !e.title.toLowerCase().includes(searchLc) &&
                            !e.categ.toLowerCase().includes(searchLc) &&
                            !address.includes(searchLc) &&
                            !excerpt.includes(searchLc) &&
                            !rue.includes(searchLc)
                        ) {
                            addThisElement = false;
                        }
                    } else {
                        if (!e.title.toLowerCase().includes(searchLc)) {
                            addThisElement = false;
                        }
                    }
                }
            }

            /** Sidebar list ignores isolate-on-open; map pins/zones still respect `isolated-element`. */
            let showInSidebar = addThisElement;
            let showOnMap = addThisElement;

            if (mapElement[0].getAttribute('isolated-element') && mapElement[0].getAttribute('isolated-element') != e.id) {
                showOnMap = false;
            }
            if (currentOpenPin) {
                if (currentOpenPin.alias && currentOpenPin.alias.includes(e.id + '|')) {
                    showOnMap = true;
                    showInSidebar = true;
                }
            }

            const card = venueCardsById.get(String(e.id)) || [];

            if (card.length != 0) {
                card.forEach(function (e2) {
                    if (e2.classList.contains('map-venue-card__is-active')) {
                        showInSidebar = true;
                    }
                    if (e.count) {
                        e2.dataset.count = e.count;
                    }
                    if (showInSidebar) {
                        e2.classList.remove('map-venue-card__hidden');
                        let title = e2.getElementsByClassName('map-venue-card__name');
                        if (searchText && searchHighlightRe) {
                            let badges = e2.getElementsByClassName('map-badge');
                            Array.prototype.forEach.call(badges, function (b) {
                                b.innerHTML = b.getAttribute('data-badge-text').replace(searchHighlightRe, `$1<strong>$2</strong>$3`);
                            });
                            if (title[0]) {
                                title[0].innerHTML = e2.getAttribute('data-title').replace(searchHighlightRe, `$1<strong>$2</strong>$3`);
                            }
                        }
                    } else {
                        e2.classList.add('map-venue-card__hidden');
                    }
                    if (e.hiddeninsidebar === true) {
                        e2.classList.add('map-venue-card__hidden');
                    }
                });
            }

            if (e.lng && e.lat) {
                if (!e.marker) {
                    let zIndex = e.zIndex ? parseInt(e.zIndex) * 10 : 0;
                    let marker = null;
                    let htmlMarker = null;

                    const pinwidth = e.pinwidth ? e.pinwidth : 45;
                    const pinheight = e.pinheight ? e.pinheight : 45;
                    const pinanchorx = e.pinanchorx ? e.pinanchorx : 22;
                    const pinanchory = e.pinanchory ? e.pinanchory : 45;

                    if (markerMode == 'advanced') {
                        coordinatesData.push({ lat: parseFloat(e.lat), lng: parseFloat(e.lng) });
                        const pinContent = document.createElement('div');
                        pinContent.style.width = `${pinwidth}px`;
                        pinContent.style.height = `${pinheight}px`;
                        pinContent.style.opacity = '0';
                        pinContent.style.cursor = 'pointer';
                        marker = new google.maps.marker.AdvancedMarkerElement({
                            position: { lat: parseFloat(e.lat), lng: parseFloat(e.lng) },
                            title: e.title,
                            content: pinContent,
                            zIndex:
                                mapElement[0].getAttribute('data-open-pin') && mapElement[0].getAttribute('data-open-pin') == e.id
                                    ? 9999 + zIndex
                                    : 999 + zIndex,
                        });
                        marker.setMap = (m) => {
                            marker.map = m;
                        };
                        marker.getMap = () => marker.map;
                        const _origAddListener = marker.addListener.bind(marker);
                        marker.addListener = (eventName, handler) => {
                            if (eventName === 'click') return _origAddListener('gmp-click', handler);
                            if (['mouseover', 'mouseout', 'focus', 'blur'].includes(eventName)) {
                                marker.content.addEventListener(eventName, handler);
                                return { remove: () => marker.content.removeEventListener(eventName, handler) };
                            }
                            return _origAddListener(eventName, handler);
                        };
                    } else {
                        coordinatesData.push({ lat: parseFloat(e.lat), lng: parseFloat(e.lng) });
                        marker = new google.maps.Marker({
                            position: { lat: parseFloat(e.lat), lng: parseFloat(e.lng) },
                            title: e.title,
                            icon: {
                                url: `data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==`,
                                scaledSize: new google.maps.Size(pinwidth, pinheight),
                                anchor: new google.maps.Point(pinanchorx, pinanchory),
                            },
                            id: e.id,
                            optimized: false,
                            canbeclicked: e.canbeclicked ? e.canbeclicked : true,
                            zIndex:
                                mapElement[0].getAttribute('data-open-pin') && mapElement[0].getAttribute('data-open-pin') == e.id
                                    ? 9999 + zIndex
                                    : 999 + zIndex,
                        });
                    }

                    let ElementIcon = e.icon;
                    if (!ElementIcon) {
                        ElementIcon = iconDefault;
                    }
                    if (mapElement[0].getAttribute('currentcateg-img') && mapElement[0].getAttribute('currentcateg-img') != '') {
                        ElementIcon = '<img src="' + mapElement[0].getAttribute('currentcateg-img') + '" loading="lazy" />';
                    }
                    const pinElement = e.pin ? e.pin : pinSVG;
                    htmlMarker = new HtmlMarker({ lat: parseFloat(e.lat), lng: parseFloat(e.lng) }, ElementIcon, e.title, e.color, pinElement);

                    e.htmlMarker = htmlMarker;

                    if (marker.infowindow !== false) {
                        marker.addListener('focus', () => {
                            closeInfoWindow();
                            const infowindowContent = generateInfoWindowContent(e);
                            infowindow.setContent(infowindowContent);
                            infowindow.open({
                                anchor: marker,
                                map,
                            });
                            infowindow.setPosition(new google.maps.LatLng(e.lat, e.lng));
                        });
                        marker.addListener('mouseover', () => {
                            clearTimeout(infowindowTimeout);
                            if (infowindow.getContent().indexOf('infowindow--active') < 0) {
                                closeInfoWindow();

                                const infowindowContent = generateInfoWindowContent(e);
                                infowindow.setContent(infowindowContent);
                                infowindow.open({
                                    anchor: marker,
                                    map,
                                });
                                infowindow.setPosition(new google.maps.LatLng(e.lat, e.lng));
                            }
                        });
                        marker.addListener('mouseout', () => {
                            if (infowindow.getContent().indexOf('infowindow--active') < 0) {
                                setInfowindowTimeout(window.setTimeout(closeInfoWindow, 250));
                            }
                        });
                    }
                    if (e.canbeclicked !== false) {
                        marker.addListener('click', () => {
                            openPin(e.id, true);
                        });
                    }
                    e.marker = marker;
                }
                if (showOnMap) {
                    fitBoundsFlag = true;
                    clusters.addMarker(e.marker, true);
                    if (checkWithinBounds(e.lat, e.lng)) {
                        bounds.extend({ lat: parseFloat(e.lat), lng: parseFloat(e.lng) });
                    }
                }
            }

            if (drawCategOnPin && e.htmlMarker && mapElement[0].getAttribute('currentcateg-img') != mapElement[0].getAttribute('currentcateg-drawnimg')) {
                let newIcon;
                if (!mapElement[0].getAttribute('currentcateg-img')) {
                    newIcon = e.icon;
                    if (!newIcon) {
                        newIcon = iconDefault;
                    }
                } else {
                    newIcon = '<img src="' + mapElement[0].getAttribute('currentcateg-img') + '" loading="lazy" />';
                }
                e.htmlMarker.icon = newIcon;
                setIconInHtmlMarker(newIcon, e.htmlMarker);
            }

            if (e.geojson) {
                let parsedJson =
                    e._parsedGeoJson || (() => {
                        try {
                            e._parsedGeoJson = JSON.parse(e.geojson);
                            return e._parsedGeoJson;
                        } catch (err) {
                            return null;
                        }
                    })();
                if (!parsedJson || !parsedJson.features || !parsedJson.features[0]) {
                    return;
                }

                /** Pin + GeoJSON on one lieu: hide polygon until that lieu is focused (less noise). GeoJSON-only rows keep previous behaviour. */
                let showGeoJsonLayer = addThisElement;
                if (addThisElement && e.lat && e.lng) {
                    const openPinId = mapElement[0].getAttribute('data-open-pin');
                    let venueFocused =
                        openPinId != null && openPinId !== '' && String(openPinId) === String(e.id);
                    if (!venueFocused && currentOpenPin && currentOpenPin.alias && currentOpenPin.alias.includes(String(e.id) + '|')) {
                        venueFocused = true;
                    }
                    if (!venueFocused && card.length) {
                        card.forEach(function (cardEl) {
                            if (cardEl.classList.contains('map-venue-card__is-active')) {
                                venueFocused = true;
                            }
                        });
                    }
                    if (!venueFocused) {
                        showGeoJsonLayer = false;
                    }
                }

                let marker = null;
                let marker_label = null;
                let styleObject = {};
                let zIndex = e.zIndex ? parseInt(e.zIndex) * 10 : 0;
                if (e.geojsonFillColor) {
                    styleObject.fillColor = e.geojsonFillColor;
                } else if (parsedJson.features[0].properties.fill) {
                    styleObject.fillColor = parsedJson.features[0].properties.fill;
                }
                if (e.geojsonFillOpacity) {
                    styleObject.fillOpacity = e.geojsonFillOpacity;
                } else if (parsedJson.features[0].properties['fill-opacity']) {
                    styleObject.fillOpacity = parsedJson.features[0].properties['fill-opacity'];
                }
                if (e.geojsonStrokeWidth) {
                    styleObject.strokeWeight = e.geojsonStrokeWidth;
                } else if (parsedJson.features[0].properties['stroke-width']) {
                    styleObject.strokeWeight = parsedJson.features[0].properties['stroke-width'];
                }
                if (e.geojsonStrokeOpacity) {
                    styleObject.strokeOpacity = e.geojsonStrokeOpacity;
                } else if (parsedJson.features[0].properties['stroke-opacity']) {
                    styleObject.strokeOpacity = parsedJson.features[0].properties['stroke-opacity'];
                }
                if (e.geojsonStrokeColor) {
                    styleObject.strokeColor = e.geojsonStrokeColor;
                } else if (parsedJson.features[0].properties.stroke) {
                    styleObject.strokeColor = parsedJson.features[0].properties.stroke;
                }
                styleObject.zIndex =
                    mapElement[0].getAttribute('data-open-pin') && mapElement[0].getAttribute('data-open-pin') == e.id ? 9999 + zIndex : 999 + zIndex;

                if (!e.zone) {
                    try {
                        marker = new google.maps.Data({
                            map: map,
                            zIndex:
                                mapElement[0].getAttribute('data-open-pin') && mapElement[0].getAttribute('data-open-pin') == e.id ? 9999 + zIndex : 999 + zIndex,
                        });
                        if (e.shape_label) {
                            var markerLabelSVG =
                                '<svg xmlns="http://www.w3.org/2000/svg"><text x="0" y="12" class="svgpathpin" style="' +
                                geoJsonLabelStyles +
                                '">' +
                                e.shape_label +
                                '</text></svg>';
                            let point = getGuaranteedInsidePoint(parsedJson.features[0]);

                            if (e.shape_label_x && e.shape_label_y) {
                                var adjustX = parseFloat(e.shape_label_x) ? parseFloat(e.shape_label_x) : 0;
                                var adjustY = parseFloat(e.shape_label_y) ? parseFloat(e.shape_label_y) : 0;
                                const plat = typeof point.lat === 'function' ? point.lat() : point.lat;
                                const plng = typeof point.lng === 'function' ? point.lng() : point.lng;
                                point = new google.maps.LatLng(plat + parseFloat(adjustY), plng + parseFloat(adjustX));
                            }

                            if (markerMode === 'advanced') {
                                const labelImg = document.createElement('img');
                                labelImg.src = 'data:image/svg+xml;charset=UTF-8;base64,' + btoa(markerLabelSVG);
                                labelImg.style.width = '20px';
                                labelImg.style.height = '20px';
                                marker_label = new google.maps.marker.AdvancedMarkerElement({
                                    position: point,
                                    map: map,
                                    title: '',
                                    content: labelImg,
                                    zIndex:
                                        mapElement[0].getAttribute('data-open-pin') && mapElement[0].getAttribute('data-open-pin') == e.id
                                            ? 9999 + zIndex
                                            : 999 + zIndex,
                                });
                                marker_label.setMap = (m) => {
                                    marker_label.map = m;
                                };
                                marker_label.getMap = () => marker_label.map;
                            } else {
                                marker_label = new google.maps.Marker({
                                    position: point,
                                    map: map,
                                    title: '',
                                    icon: {
                                        url: 'data:image/svg+xml;charset=UTF-8;base64,' + btoa(markerLabelSVG),
                                        scaledSize: new google.maps.Size(20, 20),
                                    },
                                    optimized: false,
                                    zIndex:
                                        mapElement[0].getAttribute('data-open-pin') && mapElement[0].getAttribute('data-open-pin') == e.id
                                            ? 9999 + zIndex
                                            : 999 + zIndex,
                                });
                            }
                        }
                        marker.addGeoJson(parsedJson);
                        if (styleObject) {
                            marker.setStyle(styleObject);
                        }
                    } catch (error) {
                        console.log(e.id + ' - ' + e.title + ' - ' + 'GeoJSON Invalid');
                        console.log(error);
                    }
                    e.zone = marker;
                    e.marker_label = marker_label;
                    let zoneBounds = new google.maps.LatLngBounds();
                    zoneBounds.extend({
                        lat: geoJsonBounds.yMin(parsedJson),
                        lng: geoJsonBounds.xMin(parsedJson),
                    });
                    zoneBounds.extend({
                        lat: geoJsonBounds.yMax(parsedJson),
                        lng: geoJsonBounds.xMax(parsedJson),
                    });
                    e.zoneBounds = zoneBounds;
                    if (e.canbeclicked !== false) {
                        marker.addListener('click', () => {
                            openPin(e.id, true);
                        });
                    }
                    map.data.add(marker);
                    if (marker_label) {
                        map.data.add(marker_label);
                    }
                } else {
                    marker = e.zone;
                    marker_label = e.marker_label;
                }
                if (showGeoJsonLayer && showOnMap) {
                    fitBoundsFlag = true;
                    if (checkWithinBounds(geoJsonBounds.yMin(parsedJson), geoJsonBounds.xMin(parsedJson))) {
                        bounds.extend({ lat: geoJsonBounds.yMin(parsedJson), lng: geoJsonBounds.xMin(parsedJson) });
                    }
                    if (checkWithinBounds(geoJsonBounds.yMax(parsedJson), geoJsonBounds.xMax(parsedJson))) {
                        bounds.extend({ lat: geoJsonBounds.yMax(parsedJson), lng: geoJsonBounds.xMax(parsedJson) });
                    }
                    marker.setMap(map);
                    if (marker_label) {
                        marker_label.setMap(map);
                    }
                } else {
                    marker.setMap(null);
                    if (marker_label) {
                        marker_label.setMap(null);
                    }
                }
            }
        });

        checkIfNoResults();

        if (enableLines) {
            if (!coordsFlightPolyline) {
                coordsFlightPolyline = new google.maps.Polyline({
                    path: coordinatesData,
                    geodesic: true,
                    strokeColor: '#168080',
                    strokeOpacity: 1.0,
                    strokeWeight: 3,
                });
                coordsFlightPolyline.setMap(map);
            } else {
                coordsFlightPolyline.setPath(coordinatesData);
                coordsFlightPolyline.setMap(map);
            }
        } else if (coordsFlightPolyline) {
            coordsFlightPolyline.setMap(null);
        }

        const $categCounters = document.querySelectorAll('.js-categ-counter');
        if ($categCounters.length > 0) {
            $categCounters.forEach(($el) => {
                const $parent = $el.closest('.side-panel__categ');
                if ($parent) {
                    const $results = $parent.querySelectorAll('.map-venue-card:not(.map-venue-card__hidden)');
                    let total = 0;
                    $results.forEach(($result) => {
                        if ($result.dataset.count) {
                            total += parseInt($result.dataset.count);
                        } else {
                            total++;
                        }
                    });
                    $el.innerHTML = total;
                }
            });
        }

        const hz = highlightZone;
        setHighlightZone(null);
        if (hz) {
            hz.setMap(null);
        }
        const polygon = new google.maps.Polygon(highlightPolygonConfig);
        polygon.setMap(map);
        setHighlightZone(polygon);

        clusters.render();
    }

    const afterRedoEvent = new CustomEvent('actMap:afterRedoMarkers', {
        detail: {
            mapData: actMapData,
            map: map,
            bounds: bounds,
            clusters: clusters,
        },
    });
    document.dispatchEvent(afterRedoEvent);

    let glat = 0;
    let glng = 0;
    const customGeolocArray = customGeoloc.split(',');
    if (activeGeolocalisation && navigator.geolocation) {
        fitBoundsFlag = true;

        navigator.geolocation.getCurrentPosition((position) => {
            glat = position.coords.latitude;
            glng = position.coords.longitude;
            let geolocateIconSvgCode = window.btoa(geolocateIconSvg);
            let marker;
            if (markerMode === 'advanced') {
                const geolocImg = document.createElement('img');
                geolocImg.src = `data:image/svg+xml;base64,${geolocateIconSvgCode}`;
                geolocImg.style.width = `${geolocateIconWidth}px`;
                geolocImg.style.height = `${geolocateIconHeight}px`;
                geolocImg.style.transform = `translate(${geolocateIconWidth / 2 - geolocateAnchorX}px, ${geolocateIconHeight - geolocateAnchorY}px)`;
                marker = new google.maps.marker.AdvancedMarkerElement({
                    position: { lat: glat, lng: glng },
                    content: geolocImg,
                    map: map,
                    title: 'geolocate-pin',
                });
                marker.setMap = (m) => {
                    marker.map = m;
                };
                marker.getMap = () => marker.map;
            } else {
                marker = new google.maps.Marker({
                    position: { lat: glat, lng: glng },
                    lat: glat,
                    lng: glng,
                    icon: {
                        url: `data:image/svg+xml;base64,${geolocateIconSvgCode}`,
                        scaledSize: new google.maps.Size(geolocateIconWidth, geolocateIconHeight),
                        anchor: new google.maps.Point(geolocateAnchorX, geolocateAnchorY),
                    },
                    map: map,
                    id: 'geolocate-pin',
                    optimized: false,
                });
            }
            setGeolocateUserMarker(marker);
            bounds.extend({ lat: glat, lng: glng });
            marker.setMap(map);
            fitBoundsOrCenterSingle(map, bounds, getMapPadding(), zoomOnOpen);
        });
    } else if (customGeolocArray.length == 2) {
        fitBoundsFlag = true;

        glat = parseFloat(customGeolocArray[0]);
        glng = parseFloat(customGeolocArray[1]);
        let geolocateIconSvgCode = window.btoa(geolocateIconSvg);
        let marker;
        if (markerMode === 'advanced') {
            const geolocImg = document.createElement('img');
            geolocImg.src = `data:image/svg+xml;base64,${geolocateIconSvgCode}`;
            geolocImg.style.width = `${geolocateIconWidth}px`;
            geolocImg.style.height = `${geolocateIconHeight}px`;
            geolocImg.style.transform = `translate(${geolocateIconWidth / 2 - geolocateAnchorX}px, ${geolocateIconHeight - geolocateAnchorY}px)`;
            marker = new google.maps.marker.AdvancedMarkerElement({
                position: { lat: glat, lng: glng },
                content: geolocImg,
                map: map,
                title: 'geolocate-pin',
            });
            marker.setMap = (m) => {
                marker.map = m;
            };
            marker.getMap = () => marker.map;
        } else {
            marker = new google.maps.Marker({
                position: { lat: glat, lng: glng },
                lat: glat,
                lng: glng,
                icon: {
                    url: `data:image/svg+xml;base64,${geolocateIconSvgCode}`,
                    scaledSize: new google.maps.Size(geolocateIconWidth, geolocateIconHeight),
                    anchor: new google.maps.Point(geolocateAnchorX, geolocateAnchorY),
                },
                map: map,
                id: 'geolocate-pin',
                optimized: false,
            });
        }
        setGeolocateUserMarker(marker);
        bounds.extend({ lat: glat, lng: glng });
        marker.setMap(map);
        fitBoundsOrCenterSingle(map, bounds, getMapPadding(), zoomOnOpen);
    }

    if (fitBoundsFlag && closePins) {
        fitBoundsOrCenterSingle(map, bounds, getMapPadding(), zoomOnOpen);
    }
    mapElement[0].setAttribute('currentcateg-drawnimg', mapElement[0].getAttribute('currentcateg-img'));

    google.maps.event.addListenerOnce(map, 'idle', function () {
        const completeEvent = new CustomEvent('actMap:renderComplete', {
            detail: {
                mapData: actMapData,
                map: map,
                bounds: bounds,
                clusters: clusters,
                visibleMarkers: clusters ? clusters.markers.filter((m) => m.getMap() !== null) : [],
                totalMarkers: actMapData.length,
            },
        });
        document.dispatchEvent(completeEvent);
    });
}

function clearTextualSearch(e) {
    e.preventDefault();
    if (searchInput[0]) {
        searchInput[0].value = '';
        searchInput[0].parentElement.classList.remove('map-text-search__has-data');
    }
    if (!document.querySelectorAll('.side-panel__toggle-results--active').length) {
        $side_panel.classList.remove('side-panel--fully-opened');
    }
    redoMarkers();
    triggerRedoMarkersByTextSearch();
    return false;
}

function findIndex(via, value) {
    let toReturn = '';
    actMapData.forEach(function (e) {
        if (e[via] == value) {
            toReturn = e;
        }
    });
    return toReturn;
}

export function getContrastYIQ(hexcolor) {
    var r = parseInt(hexcolor.substring(1, 3), 16);
    var g = parseInt(hexcolor.substring(3, 5), 16);
    var b = parseInt(hexcolor.substring(5, 7), 16);
    var yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 128 ? 'black' : 'white';
}

export function openDataPanel(elementToOpen) {
    if ($dataPanelTns != null) {
        $dataPanelTns.destroy();
    }
    const dataPanel = document.getElementsByClassName('js-close-data-panel');
    if (dataPanel.length != 0) {
        dataPanel[0].focus();
        dataPanel[0].blur();
    }

    const $panel = document.querySelector('.carte-layout__data-panel');
    if ($panel) {
        $panel.classList.remove('carte-layout__data-panel--hidden');
        $panel.setAttribute('data-categslugs', elementToOpen.categslugs);
    }

    let $sliderHtml = '';
    if (elementToOpen.images) {
        Array.prototype.forEach.call(elementToOpen.images, function (el) {
            let styles = '';
            if (elementToOpen.imagealign) {
                styles = 'object-position:' + elementToOpen.imagealign + ';';
            }
            $sliderHtml +=
                '<img src="' +
                el +
                '" loading="lazy" alt="' +
                elementToOpen.title +
                '" style="' +
                styles +
                '" /> ';
        });
    }
    document.querySelector('.data-panel__slider').innerHTML = $sliderHtml;

    let $badgesHtml = '';
    if (elementToOpen.categ) {
        elementToOpen.categ.split('|').forEach((el) => {
            if (!el) {
            } else if (el.includes('~')) {
                el = el.split('~');
                $badgesHtml +=
                    '<div class="map-badge" style="--added-color:' +
                    el[0] +
                    ';--contrasted-color:' +
                    getContrastYIQ(el[0]) +
                    '">' +
                    el[1] +
                    '</div>';
            } else {
                $badgesHtml += '<div class="map-badge">' + el + '</div>';
            }
        });
        if (document.querySelector('.data-panel__badges')) {
            document.querySelector('.data-panel__badges').innerHTML = $badgesHtml;
        }
    } else {
        document.querySelector('.data-panel__badges').innerHTML = '';
    }

    const $defaultFill = document.getElementsByClassName('js-default-fill');
    Array.prototype.forEach.call($defaultFill, function (el) {
        if (elementToOpen[el.getAttribute('data-filltype')]) {
            el.innerHTML = elementToOpen[el.getAttribute('data-filltype')];
            el.parentElement.setAttribute('style', '');
        } else {
            el.parentElement.setAttribute('style', 'display:none;');
        }
    });

    const $emailFill = document.getElementsByClassName('js-email-fill');
    Array.prototype.forEach.call($emailFill, function (el) {
        if (elementToOpen[el.getAttribute('data-filltype')]) {
            el.innerHTML = elementToOpen[el.getAttribute('data-filltype')];
            el.setAttribute('href', 'mailto:' + elementToOpen[el.getAttribute('data-filltype')]);
            el.parentElement.setAttribute('style', '');
        } else {
            el.parentElement.setAttribute('style', 'display:none;');
        }
    });

    const $planDetourFill = document.getElementsByClassName('js-plan-detour-fill');
    Array.prototype.forEach.call($planDetourFill, function (el) {
        if (elementToOpen[el.getAttribute('data-filltype')]) {
            el.setAttribute('href', elementToOpen[el.getAttribute('data-filltype')]);
            el.parentElement.parentElement.setAttribute('style', '');
        } else {
            el.parentElement.parentElement.setAttribute('style', 'display:none;');
        }
    });

    if (document.querySelector('.data-panel__know-more')) {
        const detailUrl = resolveDetailUrl(elementToOpen);
        if (!detailUrl) {
            document.querySelector('.data-panel__know-more').setAttribute('style', 'display:none;');
        } else {
            document.querySelector('.data-panel__know-more').setAttribute('href', detailUrl);
            document.querySelector('.data-panel__know-more').setAttribute('style', '');
        }
    }
    if (document.querySelector('.data-panel__gmaps')) {
        if (elementToOpen.lat && elementToOpen.lng) {
            document
                .querySelector('.data-panel__gmaps')
                .setAttribute(
                    'href',
                    'https://www.google.com/maps/search/' + elementToOpen.title + '/@' + elementToOpen.lat + ',' + elementToOpen.lng + ',15z',
                );
            document.querySelector('.data-panel__gmaps').setAttribute('style', '');
        } else {
            document.querySelector('.data-panel__gmaps').setAttribute('style', 'display:none;');
        }
    }

    setDataPanelTns(
        tns({
            animateDelay: 750,
            autoplayTimeout: 6000,
            controls: true,
            container: '.data-panel__slider',
            autoplay: true,
            nav: true,
        }),
    );
}

/**
 * Deep-link reload: `data-open-pin` + `?lieu=` already set the selection, but only `openPin`
 * pans the map, opens the infowindow / data panel, and syncs history like a user click.
 */
export function focusOpenPinFromUrlIfNeeded() {
    if (typeof google === 'undefined' || !google.maps || !map || !mapElement || !mapElement[0]) {
        return;
    }
    const raw = mapElement[0].getAttribute('data-open-pin');
    if (!raw || String(raw).trim() === '') {
        return;
    }
    if (!urlHasLieuQueryParam()) {
        return;
    }
    const id = parseInt(String(raw).trim(), 10);
    if (!id || Number.isNaN(id)) {
        return;
    }
    const hit = findIndex('id', id);
    if (!hit || hit === '') {
        return;
    }

    google.maps.event.addListenerOnce(map, 'idle', function () {
        openPin(id, false);
    });
}

export function openPin(id = 0, animated = true) {
    closeInfoWindow();
    const elementToOpen = findIndex('id', id);

    const beforeOpenEvent = new CustomEvent('actMap:beforeOpenPin', {
        detail: {
            pinId: id,
            pinData: elementToOpen,
            map: map,
            animated: animated,
        },
    });
    document.dispatchEvent(beforeOpenEvent);

    if (!mapElement[0].getAttribute('data-open-pin')) {
        window.localStorage.setItem('gmap-center', map.getCenter());
        window.localStorage.setItem('gmap-zoom', map.getZoom());
    }

    mapElement[0].setAttribute('data-open-pin', id);
    if ($side_panel) {
        $side_panel.classList.add('has-open-pin');
        if (typeof window !== 'undefined' && window.innerWidth < 1080 && !useDataPanel) {
            $side_panel.classList.remove('side-panel--fully-opened');
            const $toggle = document.querySelector('.side-panel__toggle-results');
            if ($toggle) $toggle.classList.remove('side-panel__toggle-results--active');
        }
    }

    const infowindowContent = generateInfoWindowContent(elementToOpen);

    if (useDataPanel) {
        openDataPanel(elementToOpen);
    }
    applyMapPadding();

    if (map.getZoom() < zoomOnOpen) {
        map.setZoom(zoomOnOpen);
    }
    if (elementToOpen.lat && elementToOpen.lng) {
        const latLng = new google.maps.LatLng(elementToOpen.lat, elementToOpen.lng);
        const padding = getMapPadding();
        const offsetX = (padding.right - padding.left) / 2;
        const offsetY = (padding.bottom - padding.top) / 2;
        const applyPaddingOffset = () => {
            if (offsetX !== 0 || offsetY !== 0) {
                map.panBy(offsetX, offsetY);
            }
        };
        if (animated) {
            map.panTo(latLng);
            google.maps.event.addListenerOnce(map, 'idle', applyPaddingOffset);
        } else {
            map.setCenter(latLng);
            applyPaddingOffset();
        }
    }
    closeInfoWindow();
    if (elementToOpen.infowindow !== false && elementToOpen.keepInfowindowClosedOnPinOpen != 1) {
        infowindow.setContent(infowindowContent.replace('"infowindow"', '"infowindow infowindow--active"'));
        infowindow.open({
            anchor: elementToOpen.marker,
            map,
        });
        infowindow.setPosition(new google.maps.LatLng(elementToOpen.lat, elementToOpen.lng));
    }

    if (elementToOpen.zoneBounds) {
        map.fitBounds(elementToOpen.zoneBounds, getMapPadding());
    }

    Array.prototype.forEach.call(mapVenueCards, function (e) {
        e.classList.remove('map-venue-card__is-active');
        if (e.getAttribute('data-id') == id) {
            e.classList.add('map-venue-card__is-active');
        }
    });
    if (elementToOpen.isolateonopen && elementToOpen.isolateonopen == 1) {
        mapElement[0].setAttribute('isolated-element', elementToOpen.id);
        if (sidePanelOfCategs[0]) {
            sidePanelOfCategs[0].classList.add('isolation-mode');
        }
    } else {
        if (sidePanelOfCategs[0]) {
            sidePanelOfCategs[0].classList.remove('isolation-mode');
        }
    }
    if (elementToOpen.htmlMarker) {
        elementToOpen.htmlMarker.setMap(map);
    } else if (elementToOpen.marker) {
        elementToOpen.marker.setMap(map);
    }
    syncHistoryOnOpenPin(id, elementToOpen);
    redoMarkers(false);

    const afterOpenEvent = new CustomEvent('actMap:afterOpenPin', {
        detail: {
            pinId: id,
            pinData: elementToOpen,
            map: map,
        },
    });
    document.dispatchEvent(afterOpenEvent);
}

/**
 * @param {*} [resetPosition] - falsy preserves map camera; omit or non-false restores from localStorage
 * @param {{ skipRedoMarkers?: boolean }} [options] - when skipRedoMarkers (from redoMarkers), skip nested redoMarkers; outer pass redraws once
 */
export function closePin(resetPosition, options = {}) {
    const skipRedoMarkers = options.skipRedoMarkers === true;
    const skipHistorySync = options.skipHistorySync === true;
    if (resetPosition !== false) {
        resetPosition = true;
    }

    if (
        !skipHistorySync &&
        typeof window !== 'undefined' &&
        window.history.state &&
        window.history.state.actCartePin != null
    ) {
        ignoreNextPopstate = true;
        window.history.back();
        return;
    }

    if (skipHistorySync) {
        restoreMapUrlIfPinnedHistory();
    }

    const beforeCloseEvent = new CustomEvent('actMap:beforeClosePin', {
        detail: {
            currentPinId: mapElement[0].getAttribute('data-open-pin'),
            map: map,
            resetPosition: resetPosition,
        },
    });
    document.dispatchEvent(beforeCloseEvent);

    mapElement[0].setAttribute('isolated-element', '');
    closeInfoWindow();
    const $panel = document.querySelector('.carte-layout__data-panel');
    $panel.classList.add('carte-layout__data-panel--hidden');
    mapElement[0].setAttribute('data-open-pin', '');
    if ($side_panel) {
        $side_panel.classList.remove('has-open-pin');
    }
    applyMapPadding();
    if (resetPosition) {
        if (window.localStorage.getItem('gmap-zoom')) {
            map.setZoom(parseFloat(window.localStorage.getItem('gmap-zoom')));
        }
        if (window.localStorage.getItem('gmap-center')) {
            let center = window.localStorage.getItem('gmap-center').replace('(', '').replace(')', '').replace(' ', '').split(',');
            map.panTo(new google.maps.LatLng(center[0], center[1]));
        }
    }
    Array.prototype.forEach.call(mapVenueCards, function (e) {
        e.classList.remove('map-venue-card__is-active');
    });
    if (!skipRedoMarkers) {
        redoMarkers(false);
    }

    syncCategoryHistoryReplace();

    const afterCloseEvent = new CustomEvent('actMap:afterClosePin', {
        detail: {
            map: map,
        },
    });
    document.dispatchEvent(afterCloseEvent);
}

if (typeof window !== 'undefined') {
    window.addEventListener('popstate', function actCarteHistoryPopstate() {
        if (ignoreNextPopstate) {
            ignoreNextPopstate = false;
            closePin(undefined, { skipHistorySync: true });
            return;
        }
        if (!mapElement || !mapElement[0]) {
            return;
        }
        const openId = mapElement[0].getAttribute('data-open-pin');
        if (!openId) {
            return;
        }
        if (!window.history.state || window.history.state.actCartePin == null) {
            closePin(undefined, { skipHistorySync: true });
        }
    });
}

/* --- sidebar-related (needs redoMarkers): kept here to avoid importing before init --- */

var searchRedoTimeout;

/**
 * Keeps `currentValue` on `.js-choose-categ` in sync with the virtual-select value for other flows.
 */
function syncCategorySelectPollerCache() {
    if (!categSelects || !categSelects.length) {
        return;
    }
    Array.prototype.forEach.call(categSelects, function (el) {
        el.setAttribute('currentValue', String(el.value));
    });
}

let categoryRedoRafId = null;

/** One redraw after category select changes; coalesces multiple VirtualSelect `setValue` `change` events. */
function scheduleRedoMarkersFromCategory() {
    syncCategorySelectPollerCache();
    if (categoryRedoRafId !== null) {
        cancelAnimationFrame(categoryRedoRafId);
    }
    categoryRedoRafId = requestAnimationFrame(function () {
        categoryRedoRafId = null;
        redoMarkers();
        syncCategoryHistoryReplace();
    });
}

export function keepPinsTabbable() {
    const pins = document.querySelectorAll('.carte-layout__map [role="button"]');
    if (pins.length != 0) {
        pins.forEach(function (e) {
            e.setAttribute('tabindex', 0);
        });
    }
}

export function triggerRedoMarkersByTextSearch() {
    if (searchInput[0]) {
        const searchEvent = new CustomEvent('actMap:search', {
            detail: {
                searchTerm: searchInput[0].value,
                map: map,
            },
        });
        document.dispatchEvent(searchEvent);
    }

    if (searchInput[0]) {
        if (searchInput[0].value) {
            if (sidePanelOfCategs[0]) {
                sidePanelOfCategs[0].classList.add('is-fully-open');
            }
            let dupesArray = Array();
            Array.prototype.forEach.call(mapVenueCards, function (e) {
                if (dupesArray.includes(e.getAttribute('data-id'))) {
                    e.classList.add('map-venue-card__is-dupe');
                } else {
                    dupesArray.push(e.getAttribute('data-id'));
                }
            });
        } else {
            if (sidePanelOfCategs[0]) {
                Array.prototype.forEach.call(mapVenueCards, function (e) {
                    e.classList.remove('map-venue-card__is-dupe');
                });
                sidePanelOfCategs[0].classList.remove('is-fully-open');
            }
        }
    }
    clearTimeout(searchRedoTimeout);
    if (searchInput[0]) {
        if (searchInput[0].value) {
            $side_panel.classList.add('side-panel--fully-opened');
            searchInput[0].parentElement.classList.add('map-text-search__has-data');
        } else {
            searchInput[0].parentElement.classList.remove('map-text-search__has-data');
            if (!document.querySelectorAll('.side-panel__toggle-results--active').length) {
                $side_panel.classList.remove('side-panel--fully-opened');
            }
        }
    }
    searchRedoTimeout = setTimeout(redoMarkers, 500);
}

export function attachMapSidebarHandlers() {
    if (categSelects && categSelects.length) {
        Array.prototype.forEach.call(categSelects, function (el) {
            el.addEventListener('change', scheduleRedoMarkersFromCategory);
        });
    }
    setInterval(keepPinsTabbable, 500);

    if (searchInput[0]) {
        searchInput[0].addEventListener('keyup', triggerRedoMarkersByTextSearch);
        searchInput[0].addEventListener('keydown', function (event) {
            if (event.keyCode == 13) {
                event.preventDefault();
                return false;
            }
        });
    }

    const searchClear = document.getElementsByClassName('js-clear-search');
    Array.prototype.forEach.call(searchClear || [], function (el) {
        el.addEventListener('click', clearTextualSearch);
    });

    Array.prototype.forEach.call(mapVenueCards, function (el) {
        el.addEventListener('click', function () {
            openPin(el.getAttribute('data-id'));
        });
    });

    if (document.querySelector('.js-close-data-panel')) {
        document.querySelector('.js-close-data-panel').addEventListener('click', closePin);
    }

    const categSelectors = document.getElementsByClassName('js-select-category');
    if (categSelectors) {
        Array.prototype.forEach.call(categSelectors, function (el) {
            el.addEventListener('click', function (e) {
                e.preventDefault();
                let categ = el.getAttribute('data-categ');
                let addingElement = false;
                let optionIsInSelects = false;
                const selects = document.getElementsByClassName('map-category-select');
                Array.prototype.forEach.call(selects, function (selEl) {
                    Array.prototype.forEach.call(selEl.options, function (option) {
                        if (option.value == categ) {
                            let ogValue = selEl.value;
                            if (selEl.value.includes(categ)) {
                                ogValue.splice(ogValue.indexOf(categ));
                                addingElement = false;
                            } else {
                                ogValue.push(categ);
                                addingElement = true;
                            }
                            selEl.setValue(ogValue);
                            optionIsInSelects = true;
                        }
                    });
                });
                if (!optionIsInSelects) {
                    if (document.getElementsByTagName('body')[0].getAttribute('data-additionnal-categ') == categ) {
                        document.getElementsByTagName('body')[0].setAttribute('data-additionnal-categ', '');
                        addingElement = false;
                    } else {
                        document.getElementsByTagName('body')[0].setAttribute('data-additionnal-categ', categ);
                        addingElement = true;
                    }
                }
                if (addingElement) {
                    mapElement[0].setAttribute('currentCateg', categ);
                    let icon = el.querySelector('.icon img');
                    if (icon) {
                        mapElement[0].setAttribute('currentCateg-img', icon.getAttribute('src'));
                    } else {
                        mapElement[0].removeAttribute('currentCateg-img');
                    }
                } else {
                    mapElement[0].removeAttribute('currentCateg');
                    mapElement[0].removeAttribute('currentCateg-img');
                }
                if (!optionIsInSelects) {
                    scheduleRedoMarkersFromCategory();
                }
                return false;
            });
        });
    }

    window.addEventListener('keydown', function checkForEscape(e) {
        if (e.keyCode == 27) {
            closePin();
        }
    });
}

function checkIfNoResults() {
    let hasResults = false;
    Array.prototype.forEach.call(mapVenueCards, function (e) {
        if (!e.classList.contains('map-venue-card__hidden')) {
            hasResults = true;
        }
    });
    noResultsText[0].innerHTML = '';
    if (noResultsText[0]) {
        if (hasResults) {
            noResultsText[0].classList.remove('side-panel__has-no-results');
            noResultsText[0].innerHTML = '';
        } else {
            noResultsText[0].classList.add('side-panel__has-no-results');
            noResultsText[0].innerHTML = noResultsTextMessage;
        }
    }
}
